import Api from 'lib/api';
import { Button, Typography } from 'lib/components';
import { TextField } from 'lib/components/FormFields';
import React, { useEffect, useRef, useState } from 'react';
import { JobInput, PatientHit } from 'types';

const CONFIRM_CODES = new Set(['confirm_create_patient', 'confirm_link_patient']);

export function isConfirmCode(message?: string): boolean {
	return Boolean(message && CONFIRM_CODES.has(message));
}

type Suggestion = {
	id: string;
	label: string;
	detail?: string;
};

type DialogState = {
	title: string;
	lines: string[];
	resolve: (value: boolean) => void;
};

export function useConfirmDialog() {
	const [dialog, setDialog] = useState<DialogState | null>(null);

	const ask = (title: string, lines: string[]) =>
		new Promise<boolean>((resolve) => {
			setDialog({ title, lines, resolve });
		});

	const close = (value: boolean) => {
		dialog?.resolve(value);
		setDialog(null);
	};

	const Dialog = () => {
		if (!dialog) return null;
		return (
			<div
				style={{
					position: 'fixed',
					inset: 0,
					background: 'rgba(0,0,0,0.45)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 50,
				}}
			>
				<div style={{ background: '#fff', padding: '1.5rem', width: '28rem', maxWidth: '90vw' }}>
					<Typography variant="h5" weight="bold">
						{dialog.title}
					</Typography>
					{dialog.lines.map((line) => (
						<Typography key={line} style={{ marginTop: '0.75rem' }}>
							{line}
						</Typography>
					))}
					<div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
						<Button title="Confirm" onClick={() => close(true)} />
						<Button title="Cancel" variant="clear" onClick={() => close(false)} />
					</div>
				</div>
			</div>
		);
	};

	return { ask, Dialog };
}

function useDebounced<T>(
	text: string,
	enabled: boolean,
	scope: string,
	load: (text: string) => Promise<T[]>
): T[] {
	const [items, setItems] = useState<T[]>([]);
	const ticket = useRef(0);

	useEffect(() => {
		const current = ++ticket.current;
		if (!enabled) {
			setItems([]);
			return;
		}
		const handle = window.setTimeout(async () => {
			const next = await load(text);
			if (ticket.current === current) {
				setItems(next);
			}
		}, 300);
		return () => window.clearTimeout(handle);
	}, [text, enabled, scope]);

	return items;
}

type FieldsProps = {
	hospitalId?: number;
	hospitalName?: string;
	patientName: string;
	setPatientName: (value: string) => void;
	patientId: string;
	setPatientId: (value: string) => void;
	patientRecordId: string | null;
	setPatientRecordId: (value: string | null) => void;
	orderingPhysician: string;
	setOrderingPhysician: (value: string) => void;
	doctorId: string | null;
	setDoctorId: (value: string | null) => void;
};

export const JobPeopleFields: React.FC<FieldsProps> = ({
	hospitalId,
	patientName,
	setPatientName,
	patientId,
	setPatientId,
	setPatientRecordId,
	orderingPhysician,
	setOrderingPhysician,
	setDoctorId,
}) => {
	const hospitalScope = String(hospitalId || '');
	const patientHits = useDebounced<PatientHit>(patientName, Boolean(hospitalId) && patientName.trim().length >= 2, hospitalScope, async (text) => {
		const { data } = await Api.patients.search(text, hospitalId, 'name');
		return data || [];
	});
	const idHits = useDebounced<PatientHit>(patientId, Boolean(hospitalId) && patientId.trim().length >= 1, hospitalScope, async (text) => {
		const { data } = await Api.patients.search(text, hospitalId, 'id');
		return data || [];
	});
	const doctorHits = useDebounced(orderingPhysician, Boolean(hospitalId) && orderingPhysician.trim().length >= 1, hospitalScope, async (text) => {
		const { data } = await Api.doctors.search(hospitalId as number, text);
		return data || [];
	});

	const pickPatient = (id: string) => {
		const match = [...patientHits, ...idHits].find((item) => item.id === id);
		if (!match) return;
		setPatientRecordId(match.id);
		setPatientName(match.displayName);
		setPatientId(match.code);
	};

	return (
		<div className="row" style={{ alignItems: 'flex-start', marginTop: '1rem', display: 'flex', flexWrap: 'wrap' }}>
			<SuggestField
				label="Patient Name:"
				value={patientName}
				placeholder="Last, First"
				disabled={!hospitalId}
				suggestions={patientHits.map((patient) => ({
					id: patient.id,
					label: patient.displayName,
					detail: patient.code,
				}))}
				onChange={(value) => {
					setPatientName(value);
					setPatientRecordId(null);
				}}
				onPick={pickPatient}
			/>
			<SuggestField
				label="Patient ID:"
				value={patientId}
				disabled={!hospitalId}
				suggestions={idHits.map((patient) => ({
					id: patient.id,
					label: patient.displayName,
					detail: patient.code,
				}))}
				onChange={(value) => {
					setPatientId(value);
					setPatientRecordId(null);
				}}
				onPick={pickPatient}
			/>
			<SuggestField
				label="Ordering Physician:"
				value={orderingPhysician}
				placeholder="Last name"
				disabled={!hospitalId}
				suggestions={doctorHits.map((doctor) => ({ id: doctor.id, label: doctor.displayName }))}
				onChange={(value) => {
					setOrderingPhysician(value);
					setDoctorId(null);
				}}
				onPick={(id) => {
					const doctor = doctorHits.find((item) => item.id === id);
					if (!doctor) return;
					setDoctorId(doctor.id);
					setOrderingPhysician(doctor.displayName);
				}}
			/>
		</div>
	);
};

function SuggestField({
	label,
	value,
	placeholder,
	disabled,
	suggestions,
	onChange,
	onPick,
}: {
	label: string;
	value: string;
	placeholder?: string;
	disabled?: boolean;
	suggestions: Suggestion[];
	onChange: (value: string) => void;
	onPick: (id: string) => void;
}) {
	const [open, setOpen] = useState(false);
	return (
		<div style={{ margin: '0.5rem', position: 'relative', minWidth: '14rem' }}>
			<TextField
				value={value}
				placeholder={placeholder}
				disabled={disabled}
				onChange={(event) => {
					onChange(event.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onBlur={() => window.setTimeout(() => setOpen(false), 200)}
				color="primary"
				variant="labelled"
				label={label}
			/>
			{open && suggestions.length > 0 && (
				<ul
					style={{
						position: 'absolute',
						zIndex: 5,
						background: '#fff',
						border: '1px solid #d7d7d7',
						margin: 0,
						padding: 0,
						listStyle: 'none',
						width: '100%',
						maxHeight: '12rem',
						overflow: 'auto',
					}}
				>
					{suggestions.map((item) => (
						<li key={item.id}>
							<button
								type="button"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => {
									onPick(item.id);
									setOpen(false);
								}}
								style={{
									display: 'block',
									width: '100%',
									textAlign: 'left',
									padding: '0.5rem',
									background: 'transparent',
									border: 0,
								}}
							>
								{item.label}
								{item.detail ? ` (${item.detail})` : ''}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export async function saveJobWithConfirm(args: {
	mode: 'create' | 'update';
	id?: number;
	body: JobInput;
	ask: (title: string, lines: string[]) => Promise<boolean>;
	hospitalName: string;
}): Promise<{ ok: boolean; message?: string }> {
	let payload: JobInput = { ...args.body };
	for (let attempt = 0; attempt < 3; attempt += 1) {
		const result =
			args.mode === 'create' ? await Api.jobs.create(payload) : await Api.jobs.update(args.id as number, payload);
		if (result.data) {
			return { ok: true };
		}
		const body = result.error?.body;
		const code = body?.code || result.error?.message;
		if (code === 'confirm_create_patient' && body?.patient && body.confirmPatient) {
			const patient = body.patient;
			const confirmed = await args.ask('Confirm new patient', [
				`Hospital: ${args.hospitalName}`,
				`Last name: ${patient.lastName}`,
				`First name: ${patient.firstName}`,
				`Patient ID: ${patient.code}`,
				'You are adding this patient.',
			]);
			if (!confirmed) return { ok: false };
			payload = {
				...payload,
				confirmPatientAction: 'create',
				confirmPatient: body.confirmPatient,
				patientRecordId: null,
			};
			continue;
		}
		if (code === 'confirm_link_patient' && body?.patient) {
			const patient = body.patient;
			const confirmed = await args.ask('Confirm existing patient', [
				`Hospital: ${args.hospitalName}`,
				`Name on file: ${patient.displayName || `${patient.lastName}, ${patient.firstName}`}`,
				`Patient ID: ${patient.code}`,
				'This patient is already on file. The job will use that name.',
			]);
			if (!confirmed) return { ok: false };
			payload = {
				...payload,
				confirmPatientAction: 'link',
				patientRecordId: patient.id || null,
				patientName: patient.displayName || `${patient.lastName}, ${patient.firstName}`,
				patientId: patient.code,
				confirmPatient: body.confirmPatient,
			};
			continue;
		}
		return { ok: false, message: result.error?.message || 'The job could not be saved.' };
	}
	return { ok: false, message: 'The job could not be saved.' };
}
